import React from 'react';
import styled from 'styled-components';
import { v4 as uuid4 } from 'uuid';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import ImageBlock from '@component/common/block/image-block';
import Gap from '@component/common/gap';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import Spinner from '@design-system/spinner';
import TextButton from '@design-system/text-button';
import BottomContainer from '@component/common/architecture/bottom-container';
import otterBookLogoImage from '@asset/image/otter-book-logo.webp';
import { ETheme } from '@datatype/type';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';

export interface INoteItem {
  key: string;
  isRepeat: boolean;
  itemContent: React.ReactNode;
}

interface IProps {
  description?: React.ReactNode;
  isPending: boolean;
  items: INoteItem[];
  emptyText: string;
  repeatStudyDisabled: boolean;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  handleRepeatToggle: (key: string, current: boolean) => void;
  handleItemClick: (key: string) => void;
  handleRemoveClick?: (key: string) => void;
  handleRepeatStudyClick: () => void;
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} 0 ${size.PAGE_PADDING};
  display: flex;
  flex-flow: column nowrap;
`;

const BottomContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: ${size.MARGIN_1};
  padding: 0 ${size.PAGE_PADDING} ${IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.MARGIN_4} ${size.PAGE_PADDING};
`;

const ItemsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 5px;
  width: 100%;
`;

const ItemContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: ${size.MARGIN_1};
  width: 100%;
  border-radius: 5px;
  border: 1px solid ${color.GRAY_BOX_BORDER_COLOR};
  padding: 5px 10px;
`;

const ItemContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  cursor: pointer;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: ${size.MARGIN_2} 0 0 0;
`;

const NoteTemplate: React.FC<IProps> = ({
  description,
  isPending,
  items,
  emptyText,
  repeatStudyDisabled,
  sentinelRef,
  scrollContainerRef,
  handleRepeatToggle,
  handleItemClick,
  handleRemoveClick,
  handleRepeatStudyClick,
}) => {
  const NoteItems = items.map((item) => (
    <ItemContainer key={uuid4()}>
      <IconBlock
        icon='repeat'
        design={{
          icon: { size: 'medium', color: item.isRepeat ? color.YELLOW_2 : color.GRAY_2, kind: 'outlined' },
          box: { event: { cursor: 'pointer' } },
        }}
        click={{ handler: () => handleRepeatToggle(item.key, item.isRepeat) }}
      />
      <ItemContentContainer onClick={() => handleItemClick(item.key)}>{item.itemContent}</ItemContentContainer>
      {handleRemoveClick && (
        <IconBlock
          icon='trash'
          design={{ icon: { size: 'small', color: color.GRAY_2 }, box: { event: { cursor: 'pointer' } } }}
          click={{ handler: () => handleRemoveClick(item.key) }}
        />
      )}
      <IconBlock
        icon='right'
        design={{ icon: { size: 'medium', color: color.GRAY_1 }, box: { event: { cursor: 'pointer' } } }}
        click={{ handler: () => handleItemClick(item.key) }}
      />
    </ItemContainer>
  ));

  return (
    <Wrapper>
      {description}
      {description && <Gap height={size.MARGIN_2} />}
      <ScrollContainer>
        <Scroll direction='y' height='100%' showScrollIndicator={true} containerRef={scrollContainerRef}>
          <ItemsContainer>
            {isPending ? (
              <EmptyContainer>
                <Spinner size='30px' color={color.BROWN_1} />
              </EmptyContainer>
            ) : NoteItems.length > 0 ? (
              <>
                {NoteItems}
                {/* 무한스크롤 sentinel — 리스트 끝 마커. viewport 진입 시 다음 페이지 자동 fetch */}
                <div ref={sentinelRef} style={{ width: '100%', height: '1px' }} />
              </>
            ) : (
              <EmptyContainer>
                <ImageBlock src={otterBookLogoImage} alt='빈 노트' design={{ image: { height: '85px' } }} />
                <TextBlock
                  text={emptyText}
                  design={{
                    text: { size: 'medium', color: color.GRAY_1 },
                    box: { width: '100%', alignX: 'center' },
                  }}
                />
              </EmptyContainer>
            )}
          </ItemsContainer>
        </Scroll>
      </ScrollContainer>
      <Gap height={size.MARGIN_2} />
      <BottomContainer
        position='relative'
        height='auto'
        backgroundColor={color.WHITE_1}
        margin={`0 -${size.PAGE_PADDING}`}>
        <BottomContentContainer>
          <TextButton
            theme={ETheme.BROWN}
            size='medium'
            shape='pill'
            width={'100%'}
            text={'반복학습하기'}
            disabled={repeatStudyDisabled}
            handleClick={handleRepeatStudyClick}
          />
        </BottomContentContainer>
      </BottomContainer>
    </Wrapper>
  );
};

export default NoteTemplate;
